// src/pages/offlineSales/terminal/components/BackdateModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { useLocalization } from '../../../../contexts/LocalizationContext';
import { getBackdateSessions } from '../../../../services/api/offlineSales/cashierSessions';
import DatePicker from '../../../../components/ui/DatePicker/DatePicker';
import Dropdown from '../../../../components/ui/Dropdown/Dropdown';
import styles from '../OfflineSalesTerminal.module.css';

const sessionLabel = (s) => {
  const opened = new Date(s.openedAt).toLocaleString();
  return `#${s.id} · ${s.cashierName || 'Cashier'} · ${opened}${s.status === 'open' ? ' (open)' : ''}`;
};

// Picks the date (and the cashier session that covered it) every following sale on this terminal
// is recorded on, until changed or reloaded. Only days with a cashier session in this shop can be picked.
// onApply(null) means "today" (no backdate); otherwise onApply({ date, sessionId }).
const BackdateModal = ({ shopId, current, onApply, onClose }) => {
  const { t } = useLocalization();
  const [sessions, setSessions] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [date, setDate] = useState(current?.date || '');
  const [sessionId, setSessionId] = useState(current?.sessionId || null);

  useEffect(() => {
    getBackdateSessions(shopId)
      .then(data => setSessions(data.sessions || []))
      .catch(err => setLoadError(err?.response?.data?.message || 'Failed to load cashier sessions'));
  }, [shopId]);

  const availableDates = useMemo(
    () => new Set((sessions || []).flatMap(s => s.dates)),
    [sessions]
  );
  const latestDate = useMemo(() => [...availableDates].sort().pop() || '', [availableDates]);
  const sessionsForDate = useMemo(
    () => (sessions || []).filter(s => s.dates.includes(date)),
    [sessions, date]
  );

  // Keep the chosen session valid for the chosen date; auto-pick when only one covers it
  useEffect(() => {
    if (sessionsForDate.some(s => s.id === sessionId)) return;
    setSessionId(sessionsForDate.length === 1 ? sessionsForDate[0].id : null);
  }, [sessionsForDate, sessionId]);

  const canApply = date && availableDates.has(date) && sessionId;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={`${styles.modal} ${styles.backdateModal}`} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{t('pos.saleDate', 'Sale Date')}</h3>
          <button onClick={onClose} className={styles.modalClose}><X size={18} /></button>
        </div>

        <div className={styles.backdateBody}>
          {loadError ? (
            <p className={styles.backdateHint}>{loadError}</p>
          ) : sessions === null ? (
            <p className={styles.backdateHint}>{t('common.loading', 'Loading...')}</p>
          ) : availableDates.size === 0 ? (
            <p className={styles.backdateHint}>
              {t('pos.noBackdateSessions', 'There are no past cashier sessions in this shop to backdate into.')}
            </p>
          ) : (
            <>
              <DatePicker
                value={date}
                onChange={setDate}
                maxDate={latestDate}
                isDateDisabled={(ymd) => !availableDates.has(ymd)}
                placeholder={t('pos.selectSessionDate', 'Select a date with a cashier session')}
              />
              {/* Only the sessions opened on the selected date; empty when there are none */}
              <Dropdown
                key={date || 'none'}
                value={sessionId || ''}
                onChange={value => setSessionId(value ? parseInt(value) : null)}
                options={sessionsForDate.map(s => ({ value: s.id, label: sessionLabel(s) }))}
                placeholder={t('pos.selectSession', 'Select cashier session')}
                disabled={sessionsForDate.length === 0}
                allowClear={false}
              />
              <p className={styles.backdateHint}>
                {t('pos.saleDateHint', 'Sales are added to that day\'s cashier session until you change the date or reload.')}
              </p>
            </>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button onClick={() => onApply(null)} className={styles.actionBtnSecondary}>
            {t('pos.useToday', 'Use Today')}
          </button>
          <button
            onClick={() => onApply({
              date,
              sessionId,
              sessionStatus: sessionsForDate.find(s => s.id === sessionId)?.status || null,
            })}
            disabled={!canApply}
            className={styles.chargeBtn}
          >
            {t('common.apply', 'Apply')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BackdateModal;
